// File Path: monorepo/web/workery-frontend/src/pages/Admin/TaskItem/Update/OrderCompletion/Step4Page.jsx
// @uix-page: TaskItemOrderCompletionStep4
// UIX Upgraded - Uses WizardFormStep whole page component

import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Navigate, useParams, useNavigate } from "react-router";
import {
  useTaskManager,
  useOrderCompletionStorage,
} from "../../../../../services/Services";
import {
  WizardFormStep,
  DetailCard,
  Alert,
  useUIXTheme,
} from "../../../../../components/UIX";
import {
  ArrowRightIcon,
  ChatBubbleBottomCenterTextIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";

// Wizard configuration
const WIZARD_STEPS = Object.freeze([
  { title: "Review", description: "Task Details", isCompleted: true },
  { title: "Status", description: "Completion Status", isCompleted: true },
  { title: "Financials", description: "Invoice Details", isCompleted: true },
  { title: "Comments", description: "Add Notes" },
  { title: "Submit", description: "Review & Complete" },
]);

// Memoized content component
const Step4Content = memo(function Step4Content() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const taskManager = useTaskManager();
  const orderCompletionStorage = useOrderCompletionStorage();
  const { getThemeClasses } = useUIXTheme();

  // Memoize theme classes
  const themeClasses = useMemo(() => ({
    textPrimary: getThemeClasses("text-primary") || "text-gray-900",
    textSecondary: getThemeClasses("text-secondary") || "text-gray-600",
    inputBorder: getThemeClasses("input-border") || "border-gray-300",
    inputFocus: getThemeClasses("input-focus") || "focus:ring-blue-500 focus:border-blue-500",
  }), [getThemeClasses]);

  const [task, setTask] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [forceURL, setForceURL] = useState("");

  // Initialize state with a function to prevent re-execution
  const [comment, setComment] = useState(() => {
    const savedState = orderCompletionStorage.getState();
    return savedState.comment || "";
  });

  // Store the saved state once
  const [savedState] = useState(() => orderCompletionStorage.getState());
  const [hasAutoGenerated, setHasAutoGenerated] = useState(false);

  const onUnauthorized = useCallback(() => {
    setForceURL("/login?unauthorized=true");
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchTask = async () => {
      try {
        setIsLoading(true);
        setErrors({});

        const taskData = await taskManager.getTaskDetail(tid, onUnauthorized);

        if (mounted) {
          setTask(taskData);

          // Auto-generate comment only once
          if (
            !hasAutoGenerated &&
            !savedState.comment &&
            savedState.hasInputtedFinancials === 1
          ) {
            let autoComment = "";
            if (savedState.paymentStatus === 1) {
              // Completed and paid
              autoComment = `Service fees for Workery Order ID ${taskData.orderWjid} paid on ${
                savedState.invoiceServiceFeePaymentDate
                  ? new Date(savedState.invoiceServiceFeePaymentDate)
                      .toISOString()
                      .slice(0, 10)
                  : new Date().toISOString().slice(0, 10)
              }.`;
            } else if (savedState.paymentStatus === 2) {
              // Completed but unpaid
              autoComment = `Service fees due for Workery Order ID ${taskData.orderWjid} completed on ${
                savedState.completionDate
                  ? new Date(savedState.completionDate)
                      .toISOString()
                      .slice(0, 10)
                  : new Date().toISOString().slice(0, 10)
              }.`;
            }

            if (autoComment) {
              setComment(autoComment);
              setHasAutoGenerated(true);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch task:", error);
        if (mounted) {
          let errorMessage = "Failed to load task details";

          if (error.code === "ERR_NETWORK") {
            errorMessage = "Network error. Please check your connection and try again.";
          } else if (error.response) {
            if (error.response.status === 404) {
              errorMessage = "Task not found";
            } else if (error.response.status === 401) {
              setForceURL("/login?unauthorized=true");
              return;
            } else if (error.response.status === 500) {
              errorMessage = "Server error. Please try again later.";
            } else if (error.response.data && error.response.data.message) {
              errorMessage = error.response.data.message;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }

          setErrors({ message: errorMessage });
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchTask();

    return () => {
      mounted = false;
    };
  }, [tid, taskManager, onUnauthorized, hasAutoGenerated, savedState]);

  const handleSubmit = useCallback(() => {
    const newErrors = {};

    // Validation
    if (!comment || comment.trim() === "") {
      newErrors.comment = "Comment is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      window.scrollTo(0, 0);
      return;
    }

    // Save to storage
    orderCompletionStorage.updateState({
      comment: comment.trim(),
    });

    // Navigate to next step
    navigate(`/admin/task/${tid}/order-completion/step-5`);
  }, [comment, orderCompletionStorage, navigate, tid]);

  const handleBack = useCallback(() => {
    navigate(`/admin/task/${tid}/order-completion/step-3`);
  }, [navigate, tid]);

  // Redirect if needed
  if (forceURL !== "") return <Navigate to={forceURL} />;

  // Action buttons
  const actions = useMemo(() => [{
    label: "Save & Continue",
    variant: "primary",
    icon: ArrowRightIcon,
    iconPosition: "right",
    onClick: handleSubmit,
  }], [handleSubmit]);

  return (
    <WizardFormStep
      wizardSteps={WIZARD_STEPS}
      currentStep={4}
      wizardTitle="Order Completion"
      wizardIcon={ClipboardDocumentCheckIcon}
      stepTitle="Add Comments"
      stepSubtitle="Add additional comments for the order"
      stepIcon={ChatBubbleBottomCenterTextIcon}
      showFormCard={false}
      contentMaxWidth="7xl"
      errors={errors}
      isLoading={isLoading}
      actions={actions}
      onBack={handleBack}
      backLabel="Back to Step 3"
      actionLayout="end"
    >
      {/* Status Alert */}
      {task && (task.status === 2 || task.isClosed === true) && (
        <Alert type="info" message="This task is archived / closed" className="mb-6" />
      )}

      <div className="space-y-6">
        <DetailCard
          title="Add Comments"
          icon={ChatBubbleBottomCenterTextIcon}
          maxWidth="full"
        >
          <div className="space-y-4">
            {/* Comment Field */}
            <div>
              <label
                htmlFor="comment-textarea"
                className={`block text-sm font-semibold ${themeClasses.textPrimary} mb-2`}
              >
                Comment <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute top-3 left-3 pointer-events-none">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  id="comment-textarea"
                  name="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Write any additional comments here..."
                  rows={6}
                  className={`w-full pl-10 pr-3 py-2 border ${
                    errors.comment ? "border-red-500" : themeClasses.inputBorder
                  } rounded-lg ${themeClasses.inputFocus} resize-none`}
                />
              </div>
              {errors.comment && (
                <p className="mt-1 text-sm text-red-600">{errors.comment}</p>
              )}
              <p className={`mt-2 text-xs ${themeClasses.textSecondary}`}>
                This comment will be attached to the order for future reference.
              </p>
            </div>

            {/* Auto-generated Comment Info */}
            {savedState.hasInputtedFinancials === 1 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <InformationCircleIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-blue-900">
                      Auto-Generated Comment
                    </p>
                    <p className="mt-1 text-sm text-blue-800">
                      A comment has been auto-generated based on your financial inputs.
                      You can modify it as needed before proceeding.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DetailCard>

        {/* Help Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-start">
            <InformationCircleIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="ml-3">
              <h4 className={`text-sm font-medium ${themeClasses.textPrimary}`}>Need Help?</h4>
              <p className={`mt-1 text-sm ${themeClasses.textSecondary}`}>
                Comments are important for maintaining a complete record of the order completion.
                Include any relevant details about the work performed, customer interactions, or special circumstances.
              </p>
            </div>
          </div>
        </div>
      </div>
    </WizardFormStep>
  );
});

function AdminTaskItemOrderCompletionStep4Page() {
  return <Step4Content />;
}

export default AdminTaskItemOrderCompletionStep4Page;
